import React, { useEffect, useState, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { Box, xcss } from '@atlaskit/primitives';
import { SuggestRelatedIssuesModalContainer } from '@atlassian/jira-ai-related-issues/src/index.tsx';
import type { FormFieldValues } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import useIsAdminHubAIEnabled from '@atlassian/jira-platform-react-hooks-use-is-admin-hub-ai-enabled/src/controllers/index.tsx';
import { useProductName } from '@atlassian/jira-tenant-context-controller/src/components/product-name/index.tsx';
import { getProductForAdminHubAi } from './utils.tsx';

type Props = {
	getFormState: () => FormFieldValues;
	selectedProjectId?: string;
};
const getCustomRelatedIssueSearchPayload = (selectedProjectId: string, summary: string) => {
	return {
		project_id: selectedProjectId,
		issue_summary: summary,
	};
};

export const SimilarWorkItemInGIC = (props: Props) => {
	const summary = props.getFormState()?.values?.summary || '';
	const selectedProjectId = props.selectedProjectId || '';

	const [showComponent, setShowComponent] = useState(false);
	const [debouncedSummary, setDebouncedSummary] = useState('');
	const prevSummaryRef = useRef(summary);

	const productName = useProductName();
	const adminHubAiProductName = getProductForAdminHubAi(productName);

	const { data: isAdminHubAiEnabled, fetchAIEnablement } =
		useIsAdminHubAIEnabled(adminHubAiProductName);

	// check if AI is enabled for the selected project/product
	useEffect(() => {
		fetchAIEnablement();
	}, [fetchAIEnablement]);

	const debouncedUpdate = useMemo(
		() =>
			debounce((value) => {
				setDebouncedSummary(value);
				if (value) {
					setShowComponent(true);
				} else {
					setShowComponent(false);
				}
			}, 1000),
		[],
	);

	useEffect(() => {
		if (summary !== prevSummaryRef.current) {
			debouncedUpdate(summary);
			prevSummaryRef.current = summary;
		}
		return () => {
			debouncedUpdate.cancel();
		};
	}, [summary, debouncedUpdate]);
	const customRelatedIssueSearchPayload = useMemo(() => {
		return getCustomRelatedIssueSearchPayload(selectedProjectId, debouncedSummary);
	}, [selectedProjectId, debouncedSummary]);

	return (
		showComponent &&
		isAdminHubAiEnabled && (
			<Box xcss={relatedIssuesContainerStyles}>
				<SuggestRelatedIssuesModalContainer
					issueKey=""
					onIssueLinkSave={noop}
					existingIssues={['']}
					customRelatedIssueSearchPayload={customRelatedIssueSearchPayload}
					invokedFromSource="globalIssueCreate"
				/>
			</Box>
		)
	);
};
const relatedIssuesContainerStyles = xcss({
	marginTop: 'space.300',
});
